import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { ItemBatch, ItemBatchFormData } from '../types/item-batch.types';
import { itemBatchApi } from '../api/itemBatchApi';

interface ItemBatchesSectionProps {
  itemId: string;
  itemName: string;
}

const ItemBatchesSection: React.FC<ItemBatchesSectionProps> = ({ itemId, itemName }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [batches, setBatches] = useState<ItemBatch[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingBatch, setEditingBatch] = useState<ItemBatch | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  
  // Form state
  const [formData, setFormData] = useState<ItemBatchFormData>({
    itemId,
    batchNumber: '',
    expiryDate: '',
    status: 'ACTIVE',
    isActive: true,
  });

  // Format date to a readable format
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Check if batch is expiring soon (within 30 days)
  const isExpiringSoon = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    return expiry <= thirtyDaysFromNow && expiry >= now;
  };

  // Check if batch is expired
  const isExpired = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    return expiry < now;
  };

  // Get status color
  const getStatusColor = (batch: ItemBatch) => {
    if (!batch.isActive) return 'default';
    if (batch.status === 'EXPIRED' || isExpired(batch.expiryDate)) return 'error';
    if (batch.status === 'RECALLED') return 'warning';
    if (batch.status === 'DISPOSED') return 'default';
    if (isExpiringSoon(batch.expiryDate)) return 'warning';
    return 'success';
  };

  // Get status label
  const getStatusLabel = (batch: ItemBatch) => {
    if (!batch.isActive) return 'Inactive';
    if (isExpired(batch.expiryDate)) return 'Expired';
    if (isExpiringSoon(batch.expiryDate)) return 'Expiring Soon';
    return batch.status;
  };

  // Load batches
  const loadBatches = async () => {
    try {
      setLoading(true);
      const data = await itemBatchApi.getItemBatchesByItemId(itemId);
      setBatches(data);
    } catch (error) {
      console.error('Error loading batches:', error);
      enqueueSnackbar('Failed to load batches', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      if (editingBatch) {
        // Update existing batch
        const updatedBatch = await itemBatchApi.updateItemBatch(editingBatch.id, formData);
        setBatches(prev => prev.map(batch => 
          batch.id === editingBatch.id ? updatedBatch : batch
        ));
        enqueueSnackbar('Batch updated successfully', { variant: 'success' });
      } else {
        // Create new batch
        const newBatch = await itemBatchApi.createItemBatch(formData);
        setBatches(prev => [newBatch, ...prev]);
        enqueueSnackbar('Batch created successfully', { variant: 'success' });
      }
      
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving batch:', error);
      enqueueSnackbar(error.message || 'Failed to save batch', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (batch: ItemBatch) => {
    if (!window.confirm(`Are you sure you want to delete batch "${batch.batchNumber}"?`)) {
      return;
    }

    try {
      await itemBatchApi.deleteItemBatch(batch.id);
      setBatches(prev => prev.filter(b => b.id !== batch.id));
      enqueueSnackbar('Batch deleted successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error deleting batch:', error);
      enqueueSnackbar(error.message || 'Failed to delete batch', { variant: 'error' });
    }
  };

  // Handle open dialog
  const handleOpenDialog = (batch?: ItemBatch) => {
    if (batch) {
      setEditingBatch(batch);
      setFormData({
        itemId: batch.itemId,
        batchNumber: batch.batchNumber,
        expiryDate: batch.expiryDate || '',
        status: batch.status,
        isActive: batch.isActive,
      });
    } else {
      setEditingBatch(null);
      setFormData({
        itemId,
        batchNumber: '',
        expiryDate: '',
        status: 'ACTIVE',
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBatch(null);
    setFormData({
      itemId,
      batchNumber: '',
      expiryDate: '',
      status: 'ACTIVE',
      isActive: true,
    });
  };

  // Load batches on component mount
  useEffect(() => {
    loadBatches();
  }, [itemId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Item Batches</Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={loadBatches} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ ml: 1 }}
            >
              Add Batch
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : batches.length === 0 ? (
          <Box textAlign="center" p={3}>
            <Typography variant="body2" color="textSecondary">
              No batches found for this item
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Batch Number</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {batch.batchNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {isExpired(batch.expiryDate) && (
                          <WarningIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        {isExpiringSoon(batch.expiryDate) && !isExpired(batch.expiryDate) && (
                          <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography variant="body2">
                          {formatDate(batch.expiryDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(batch)}
                        color={getStatusColor(batch)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(batch.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(batch)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(batch)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingBatch ? 'Edit Batch' : 'Add New Batch'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Batch Number"
                  value={formData.batchNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, batchNumber: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  label="Expiry Date"
                  value={formData.expiryDate ? new Date(formData.expiryDate) : null}
                  onChange={(date) => setFormData(prev => ({ 
                    ...prev, 
                    expiryDate: date ? date.toISOString().split('T')[0] : '' 
                  }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="EXPIRED">Expired</MenuItem>
                    <MenuItem value="RECALLED">Recalled</MenuItem>
                    <MenuItem value="DISPOSED">Disposed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saving || !formData.batchNumber}
            >
              {saving ? <CircularProgress size={20} /> : (editingBatch ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
};

export default ItemBatchesSection;
